<template>
    <Card class="mb-4">
        <template v-slot:title>{{ __('stock-item.article_data_coverage') }}</template>
        <template v-slot:icon><i class="fas fa-compass"></i></template>
        <template v-slot:toolbar>
            <Link :href="$route('store.articles.index', {store: $page.props.store})"
                  as="button"
                  class="btn btn-primary px-5">
                <i class="fas fa-list"/>{{ __('general.thing.go_to', {thing: __('navigation.index_article')}) }}
            </Link>
        </template>
        <div>
            <div class="d-flex justify-content-center align-items-center gap-4 mb-6">
                <div class="d-flex align-items-center">
                    <span class="badge bg-success mr-2 pt-1">&nbsp;</span>
                    <span class="font-weight-bold">{{ __('stock-item.article_complete_dataset') }}  <br>
                            <span class="font-weight-normal font-italic">{{ __('stock-item.article_all_channels') }}</span>
                        </span>
                </div>
                <div class="d-flex align-items-center">
                    <span class="badge bg-warning mr-2 pt-1">&nbsp;</span>
                    <span class="font-weight-bold">{{ __('stock-item.missing_or_incomplete_article_data') }}<br>
                            <span class="font-weight-normal font-italic">{{ __('stock-item.article_visible_not_sellable') }}</span>
                        </span>
                </div>
            </div>

            <div class="progress mx-10" style="height: 30px;">
                <div
                    class="progress-bar bg-success text-black text-lg font-weight-bold"
                    role="progressbar"
                    :style="{ width: fullContentArticlePercentage + '%' }"
                    :aria-valuenow="fullContentArticleCount"
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    {{ fullContentArticleCount }}
                </div>
                <div
                    class="progress-bar bg-warning text-black text-lg font-weight-bold"
                    role="progressbar"
                    :style="{ width: missingArticlePercentage + missingContentArticlePercentage + '%' }"
                    :aria-valuenow="missingArticleCount+ missingContentArticlePercentage"
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    {{ missingArticleCount }}
                </div>
            </div>
        </div>

        <Card class="my-12 text-center">
            <div class="mb-6">
                <div class="text-xl">{{ __('stock-item.total_article_count') }}</div>
                <div class="row mx-auto justify-content-center gap-12 font-weight-bold text-2xl">
                    <div>{{ contentIssueCounts.total_stocks }}</div>
                    <div>100 %</div>
                </div>
            </div>

            <div class="my-6">
                <div class="text-xl">{{ __('stock-item.article_with_complete_dataset') }}</div>
                <div class="row mx-auto justify-content-center gap-12 font-weight-bold text-2xl">
                    <div>{{ fullContentArticleCount }}</div>
                    <div>{{ (fullContentArticlePercentage).toFixed(2) }} %</div>
                </div>
            </div>
            <div class="my-6">
                <div class="text-xl">{{ __('stock-item.article_with_incomplete_dataset') }}</div>
                <div class="row mx-auto justify-content-center gap-12 font-weight-bold text-2xl">
                    <div>{{ missingContentArticleCount }}</div>
                    <div>{{ (missingContentArticlePercentage).toFixed(2) }} %</div>
                </div>
            </div>
            <div class="mb-6">
                <div class="text-xl">{{ __('stock-item.article_with_missing_dataset') }}</div>
                <div class="row mx-auto justify-content-center gap-12">
                    <div class="font-weight-bold text-xl">{{ missingArticleCount }}</div>
                    <div class="font-weight-bold text-xl">{{ (missingArticlePercentage).toFixed(2) }} %</div>
                </div>
            </div>

        </Card>

        <div v-if="missingContentArticleCount > 0">
            <h3 class="text-center btn-light-primary">{{ __('stock-item.known_article_missing_data') }}</h3>
            <div class="d-flex flex-column justify-content-center align-items-center my-6">
                <PieChart :colors="['#ea4b4b', '#f39e3a', '#4fa0d7']"
                          :chart-labels="Object.keys(subIssues)"
                          height="560px"
                          width="560px"
                          :series-data="subIssues"
                          type="donut"
                          :show-legend="false"/>

                <!--         CUSTOM LEGEND       -->
                <div class="d-flex justify-content-center align-items-center gap-8 my-4">
                    <Tooltip class="w-300px" position="bottom">
                        <template #trigger>
                            <div class="d-flex align-items-center">
                                <span class="badge mr-2 pt-1" style="background-color: #ea4b4b !important">&nbsp</span>
                                <span class="font-weight-bold">{{ __(`stock-item.${ContentIssueEnum.MISSING_DESCRIPTION}`) }}</span>
                            </div>
                        </template>
                        <div v-html="__(`stock-item.${ContentIssueEnum.MISSING_DESCRIPTION}_tooltip`)"></div>
                    </Tooltip>

                    <Tooltip class="w-300px" position="bottom">
                        <template #trigger>
                            <div class="d-flex align-items-center">
                                <span class="badge  mr-2 pt-1" style="background-color: #4fa0d7 !important">&nbsp</span>
                                <span class="font-weight-bold">{{ __(`stock-item.${ContentIssueEnum.MISSING_MANUFACTURER_INFO}`) }}</span>
                            </div>
                        </template>
                        <div v-html="__(`stock-item.${ContentIssueEnum.MISSING_MANUFACTURER_INFO}_tooltip`)">
                        </div>
                    </Tooltip>
                    <Tooltip class="w-300px" position="bottom">
                        <template #trigger>
                            <div class="d-flex align-items-center">
                                <span class="badge bg-success mr-2 pt-1" style="background-color: #f39e3a !important">&nbsp</span>
                                <span class="font-weight-bold">{{ __(`stock-item.${ContentIssueEnum.MISSING_IMAGE}`) }}</span>
                            </div>
                        </template>
                        <div v-html="__(`stock-item.${ContentIssueEnum.MISSING_IMAGE}_tooltip`)"></div>
                    </Tooltip>
                </div>
            </div>
        </div>

        <!--    CATEGORY CHART    -->
        <Card v-if="categoriesWithProductCount.length > 0" class="my-12">
            <h3 class="text-center">{{ __('stock-item.article_complete_data_category') }}</h3>

            <BarChart :title="__('stock-item.article_complete_data_category')"
                      :chart-data="categoriesWithProductCount"
                      horizontal>
            </BarChart>
        </Card>

    </Card>
</template>

<script>
import Card from '@components/Cards/Card.vue';
import BarChart from '@components/Charts/BarChart.vue';
import PieChart from '@components/PieChart.vue';
import {ContentIssueEnum} from '@generated/contentIssueEnum';
import StoreLayout from '@layouts/StoreLayout.vue';
import {Link} from '@inertiajs/vue2';
import Tooltip from '@components/Tooltip.vue';

export default {
    layout: StoreLayout,
    components: {Tooltip, PieChart, BarChart, Card, Link},
    props: {
        contentIssueCounts: {
            total_stocks: Number,
            missing_article_count: Number,
            missing_description_count: Number,
            missing_image_count: Number,
            missing_manufacturer_info_count: Number,
        },
        categoriesWithProductCount: Array,
    },

    computed: {
        ContentIssueEnum() {
            return ContentIssueEnum;
        },

        subIssues() {
            return {
                [this.__(`stock-item.${ContentIssueEnum.MISSING_DESCRIPTION}`)]: this.contentIssueCounts.missing_description_count,
                [this.__(`stock-item.${ContentIssueEnum.MISSING_IMAGE}`)]: this.contentIssueCounts.missing_image_count,
                [this.__(`stock-item.${ContentIssueEnum.MISSING_MANUFACTURER_INFO}`)]: this.contentIssueCounts.missing_manufacturer_info_count,
            };
        },

        fullContentArticleCount() {
            return this.contentIssueCounts.total_stocks - this.contentIssueCounts.missing_article_count - this.missingContentArticleCount;
        },
        missingArticleCount() {
            return this.contentIssueCounts.missing_article_count;
        },
        missingContentArticleCount() {
            return Math.max(this.contentIssueCounts.missing_image_count, this.contentIssueCounts.missing_description_count,
                this.contentIssueCounts.missing_manufacturer_info_count);
        },
        fullContentArticlePercentage() {
            if (this.contentIssueCounts.total_stocks === 0) return 0;
            return (this.fullContentArticleCount / this.contentIssueCounts.total_stocks) * 100;
        },
        missingArticlePercentage() {
            if (this.contentIssueCounts.total_stocks === 0) return 0;
            return (this.missingArticleCount / this.contentIssueCounts.total_stocks) * 100;
        },
        missingContentArticlePercentage() {
            if (this.contentIssueCounts.total_stocks === 0) return 0;
            return (this.missingContentArticleCount / this.contentIssueCounts.total_stocks) * 100;
        },
    },
};
</script>
